import React, { useEffect, useMemo, useRef, useState, type JSX } from 'react';

import {
  PlatformTableFiltersParameter,
  PlatformTableRelation,
  usePlatformExecuteAdHocQueryTeam,
  usePlatformTableSchemaTeam,
} from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { GridColDef } from '@mui/x-data-grid';
import pluralize from 'pluralize';

import { Loader, usePagination } from 'shared/base';

import { CLOUD_ASSET_UNIQUE_KEY } from '../../../../constants/data';
import { useTableStateContext } from '../../../../hooks/useTableStateContext';
import { DisplayColumns, RelatedRecordsTableState } from '../../../../types';
import { countFormat, getDefaultColumns, getHeaderName } from '../../../../utils/table';

import { ColumnToggle } from '../../../controls/columnToggle';
import { customColumns } from '../../../table/columns';

export const RelatedResourceTable = ({
  filter = [],
  resource,
}: {
  filter?: PlatformTableFiltersParameter;
  resource: PlatformTableRelation;
}) => {
  const { detailIds, setDetailIds } = useTableStateContext();
  const { apiPage, perPage, setPerPage, setTablePage, tablePage } = usePagination({
    perPageOptions: [5, 10, 25, 50, 100],
    tableId: 'asset-inventory-related-resources',
  });
  const { currentMembership } = useBaseAuthenticated();
  const [displayColumns, setDisplayColumns] = useState<DisplayColumns>(new Set());
  const defaultDisplayColumns = useRef<DisplayColumns>(new Set());

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setTablePage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPerPage(Number.parseInt(event.target.value, 10));
    setTablePage(0);
  };

  const {
    data,
    error,
    isFetching,
    isLoading: dataLoading,
  } = usePlatformExecuteAdHocQueryTeam(
    currentMembership.team.name ?? '',
    { query: resource?.query },
    { filter, page: apiPage, per_page: perPage },
    {
      query: {
        enabled: !!resource,
        keepPreviousData: true,
      },
    },
  );

  const { data: schemaResponse, isLoading: schemaLoading } = usePlatformTableSchemaTeam(
    currentMembership.team.name,
    resource?.table_name,
    {
      query: {
        staleTime: Infinity,
      },
    },
  );

  const { columns, lastPage, rows, totalCount }: RelatedRecordsTableState = useMemo(() => {
    if (data?.data?.data) {
      const responseData = data.data.data;
      const responseMetadata = data.data.metadata;

      const dataState = {
        columns: responseData.columns
          .filter(({ name }) => !name.includes('_cq_'))
          .map((col) => {
            const name = col.name.replace(`${resource?.table_name}.`, '');

            return { ...col, field: name, headerName: getHeaderName(name), name };
          })
          .sort((a, b) => a.name.localeCompare(b.name)),
        lastPage: responseMetadata.last_page ?? 0,
        rows: responseData.rows.map((row: unknown[]) => {
          const rowObj = {} as Record<string, unknown>;
          for (const [i, element] of row.entries()) {
            rowObj[responseData.columns[i].name.replace(`${resource?.table_name}.`, '') as string] =
              element;
          }

          return rowObj;
        }),
        totalCount: responseMetadata.total_count ?? -1,
      };

      return dataState;
    } else {
      return {
        columns: [],
        lastPage: 0,
        rows: [],
        totalCount: -1,
      };
    }
  }, [data, resource?.table_name]);

  useEffect(() => {
    if (schemaResponse && columns) {
      defaultDisplayColumns.current = new Set(
        getDefaultColumns(
          columns.map(({ name }) => ({ field: name })),
          [],
          schemaResponse?.data?.default_columns,
        ),
      );
      setDisplayColumns(defaultDisplayColumns.current);
    }
  }, [schemaResponse, columns]);

  const filteredColumns = useMemo(() => {
    return columns.filter(({ name }) => displayColumns.has(name));
  }, [columns, displayColumns]);

  const loading = schemaLoading || dataLoading;
  if (loading && totalCount < 0) {
    return <Loader text={`Loading related ${resource.label} records.`} />;
  }

  return (
    <Accordion
      defaultExpanded={totalCount > 0}
      sx={{
        '&.Mui-expanded': {
          borderRadius: 1,
        },
      }}
    >
      <AccordionSummary
        aria-controls="panel1-content"
        expandIcon={<ExpandMoreIcon />}
        id="panel1-header"
        sx={{
          '&.Mui-expanded': { minHeight: 'unset' },
          '>.MuiAccordionSummary-content': { marginY: totalCount === 0 ? 0.5 : 0 },
          minHeight: 'unset',
        }}
      >
        <Stack alignItems="center" direction="row" justifyContent="space-between" width="100%">
          <Stack direction="row" gap={1}>
            <Typography variant="tableHeader">{pluralize(resource?.label)}</Typography>
            <Typography variant="tableHeader">
              {`(${countFormat(totalCount > -1 ? totalCount : 0)})`}
            </Typography>
          </Stack>
          <Stack direction="row" gap={1}>
            {error && (
              <Typography color="error" variant="tableHeader">
                Error fetching related records
              </Typography>
            )}
            {totalCount > 0 && (
              <ColumnToggle
                columns={columns as unknown as GridColDef[]}
                displayColumns={displayColumns}
                overwriteDisplayColumns={setDisplayColumns}
                resetDisplayColumns={() => setDisplayColumns(defaultDisplayColumns.current)}
                setDisplayColumns={(toggleColumn) => {
                  const copiedDisplayColumns = new Set(displayColumns) as DisplayColumns;
                  if (copiedDisplayColumns.has(toggleColumn)) {
                    copiedDisplayColumns.delete(toggleColumn);
                  } else {
                    copiedDisplayColumns.add(toggleColumn);
                  }
                  setDisplayColumns(copiedDisplayColumns);
                }}
              />
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      {totalCount > 0 ? (
        <Stack>
          <TableContainer>
            <Table
              size="small"
              stickyHeader={true}
              sx={({ palette }: any) => ({
                '& .MuiTableCell-head': {
                  backgroundColor: palette.background.paperGlass,
                  borderTop: `1px solid ${palette.divider}`,
                },
                '& .MuiTableCell-root': {
                  backgroundColor: palette.background.paperGlass,
                  borderBottom: `1px solid ${palette.divider}`,
                  borderRadius: `0 !important`,
                },
              })}
            >
              <TableHead>
                <TableRow>
                  {filteredColumns.map(({ headerName, name }) => (
                    <TableCell key={name}>{headerName}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody sx={{ height: '100%', overflowY: 'auto', width: '100%' }}>
                {rows.map((row) => {
                  return (
                    <TableRow
                      key={row[`${resource.table_name}.${CLOUD_ASSET_UNIQUE_KEY}`] as string}
                      hover={true}
                      onClick={() => {
                        setDetailIds([...(detailIds ?? []), row[CLOUD_ASSET_UNIQUE_KEY] as string]);
                      }}
                    >
                      {filteredColumns.map(({ name }) => {
                        if (isFetching) {
                          return (
                            <TableCell key={name}>
                              <Skeleton />
                            </TableCell>
                          );
                        }
                        const CustomColumn = customColumns[name] as (props: {
                          value: any;
                        }) => JSX.Element;

                        return (
                          <TableCell key={name}>
                            {CustomColumn ? (
                              <CustomColumn value={row[name]} />
                            ) : (
                              row[name]?.toString()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            count={totalCount}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            page={tablePage}
            rowsPerPage={perPage}
            rowsPerPageOptions={[5, 10, 25, 50, { label: 'All', value: 1000 }]}
            slotProps={{
              actions: {
                nextButton: {
                  disabled: lastPage <= apiPage,
                },
              },
            }}
          />
        </Stack>
      ) : (
        <AccordionDetails>
          <Typography variant="body1">There are no related {resource.label} records.</Typography>
        </AccordionDetails>
      )}
    </Accordion>
  );
};
